import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  deleteAssociation,
  getAssociationById,
  getClientsKanbanBoard,
  moveAssociationKanban,
  saveAssociation,
  updateAssociation,
} from "../../../../../services/associationsServices";

import {
  DEFAULT_PRODUCT_OPTIONS,
  EMPTY_CLIENT_STATS,
} from "../data/suiviClientsData";

import {
  computePositionFromNeighbors,
  filterClientColumns,
  getUniqueOptionsFromColumns,
  mergeOptions,
  moveCardBetweenColumns,
  reorderCards,
} from "../utils/suiviClientsUtils";

const DEFAULT_FILTERS = {
  q: "",
  produit_asso: "all",
  discipline_asso: "all",
  favorite: "all",
  hideEmptyColumns: false,
  sort_relance: "position",
};

export default function useSuiviClientsKanban() {
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(EMPTY_CLIENT_STATS);
  const [columns, setColumns] = useState([]);

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [allCardsExpanded, setAllCardsExpanded] = useState(false);
  const [expandedCardIds, setExpandedCardIds] = useState(() => new Set());

  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [associationToDelete, setAssociationToDelete] = useState(null);
  const [createColumn, setCreateColumn] = useState(null);

  const filteredColumns = useMemo(() => {
    const filtered = filterClientColumns(columns, filters);
    const hasSearch = String(filters.q || "").trim().length > 0;

    if (hasSearch || filters.hideEmptyColumns) {
      return filtered.filter((column) => (column.cards || []).length > 0);
    }

    return filtered;
  }, [columns, filters]);

  const produitOptions = useMemo(() => {
    return mergeOptions(
      DEFAULT_PRODUCT_OPTIONS,
      getUniqueOptionsFromColumns(columns, "produit_asso"),
    );
  }, [columns]);

  const disciplineOptions = useMemo(() => {
    return getUniqueOptionsFromColumns(columns, "discipline_asso");
  }, [columns]);

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getClientsKanbanBoard();

      setBoard(data.board || null);
      setStats(data.stats || EMPTY_CLIENT_STATS);
      setColumns(Array.isArray(data.columns) ? data.columns : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement du suivi clients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const updateFilter = useCallback((name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const toggleAllCards = useCallback(() => {
    setAllCardsExpanded((current) => !current);
    setExpandedCardIds(new Set());
  }, []);

  const toggleCardExpanded = useCallback((associationId) => {
    if (!associationId) return;

    setExpandedCardIds((current) => {
      const next = new Set(current);
      const key = String(associationId);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const openAssociation = useCallback(async (association) => {
    if (!association?.id) return;

    try {
      setSelectedAssociation(association);
      setDetailLoading(true);

      const freshAssociation = await getAssociationById(association.id);

      if (freshAssociation?.id) {
        setSelectedAssociation({
          ...association,
          ...freshAssociation,
          prospects:
            freshAssociation.prospects ||
            freshAssociation.Prospects ||
            association.prospects ||
            association.Prospects ||
            [],
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Impossible de charger le détail de l'association.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const replaceAssociationInColumns = useCallback((updatedAssociation) => {
    if (!updatedAssociation?.id) return;

    setColumns((currentColumns) =>
      currentColumns.map((column) => ({
        ...column,
        cards: (column.cards || []).map((association) =>
          Number(association.id) === Number(updatedAssociation.id)
            ? { ...association, ...updatedAssociation }
            : association,
        ),
      })),
    );

    setSelectedAssociation((current) =>
      Number(current?.id) === Number(updatedAssociation.id)
        ? {
            ...current,
            ...updatedAssociation,
            prospects:
              updatedAssociation.prospects ||
              updatedAssociation.Prospects ||
              current.prospects ||
              current.Prospects ||
              [],
          }
        : current,
    );
  }, []);

  const sortCardsByPosition = useCallback((cards = []) => {
    return [...cards].sort((a, b) => {
      const posA = Number(a.kanban_position || 0);
      const posB = Number(b.kanban_position || 0);

      if (posA !== posB) return posA - posB;

      return String(a.nom_asso || "").localeCompare(
        String(b.nom_asso || ""),
        "fr",
      );
    });
  }, []);

  const optimisticMoveAssociation = useCallback(
    ({
      associationId,
      destinationColumnId,
      destinationStatus,
      newPosition,
    }) => {
      setColumns((currentColumns) => {
        let movedAssociation = null;

        const cleanedColumns = currentColumns.map((column) => {
          const nextCards = (column.cards || []).filter((association) => {
            const isMoved = Number(association.id) === Number(associationId);

            if (isMoved) {
              movedAssociation = association;
            }

            return !isMoved;
          });

          return {
            ...column,
            cards: nextCards,
            count: nextCards.length,
          };
        });

        if (!movedAssociation) return currentColumns;

        return cleanedColumns.map((column) => {
          if (column.id !== destinationColumnId) return column;

          const updatedMovedAssociation = {
            ...movedAssociation,
            statut_asso: destinationStatus,
            kanban_position: newPosition,
          };

          const nextCards = sortCardsByPosition([
            ...(column.cards || []),
            updatedMovedAssociation,
          ]);

          return {
            ...column,
            cards: nextCards,
            count: nextCards.length,
          };
        });
      });
    },
    [sortCardsByPosition],
  );

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;

      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const associationId = Number(
        String(draggableId).replace("association-", ""),
      );

      const sourceColumn = filteredColumns.find(
        (column) => column.id === source.droppableId,
      );

      const destinationColumn = filteredColumns.find(
        (column) => column.id === destination.droppableId,
      );

      if (!sourceColumn || !destinationColumn) return;

      const previousColumns = columns;

      try {
        let orderedDestinationCards = [];

        if (source.droppableId === destination.droppableId) {
          orderedDestinationCards = reorderCards(
            sourceColumn.cards,
            source.index,
            destination.index,
          );
        } else {
          const moveResult = moveCardBetweenColumns({
            sourceColumn,
            destinationColumn,
            sourceIndex: source.index,
            destinationIndex: destination.index,
          });

          orderedDestinationCards = moveResult.orderedDestinationCards;
        }

        const newPosition = computePositionFromNeighbors(
          orderedDestinationCards,
          destination.index,
        );

        optimisticMoveAssociation({
          associationId,
          destinationColumnId: destinationColumn.id,
          destinationStatus: destinationColumn.status,
          newPosition,
        });

        const updatedAssociation = await moveAssociationKanban(associationId, {
          statut_asso: destinationColumn.status,
          kanban_position: newPosition,
        });

        if (updatedAssociation?.id) {
          replaceAssociationInColumns(updatedAssociation);
        }

        toast.success("Carte déplacée.");
      } catch (error) {
        console.error(error);
        setColumns(previousColumns);
        toast.error("Erreur lors du déplacement de la carte.");
      }
    },
    [
      columns,
      filteredColumns,
      optimisticMoveAssociation,
      replaceAssociationInColumns,
    ],
  );

  const handleSaveAssociation = useCallback(
    async (associationId, patch) => {
      if (!associationId) return;

      try {
        setSaving(true);

        const updatedAssociation = await updateAssociation(associationId, patch);

        if (updatedAssociation?.id) {
          replaceAssociationInColumns(updatedAssociation);
        }

        await loadBoard();

        toast.success("Association mise à jour.");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la sauvegarde.");
      } finally {
        setSaving(false);
      }
    },
    [loadBoard, replaceAssociationInColumns],
  );

  const handleCreateAssociation = useCallback(
    async (payload = {}) => {
      if (!createColumn) return;

      try {
        setSaving(true);

        const targetColumn = columns.find(
          (column) => column.id === createColumn.id,
        );

        const maxPosition = Math.max(
          0,
          ...((targetColumn?.cards || []).map((association) =>
            Number(association.kanban_position || 0),
          ) || []),
        );

        const createdAssociation = await saveAssociation({
          nom_asso: payload.nom_asso || "Nouvelle association",
          produit_asso: payload.produit_asso || "",
          discipline_asso: payload.discipline_asso || "",
          date_relance_asso: payload.date_relance_asso || null,
          statut_asso: createColumn.status,
          kanban_position: maxPosition + 1000,
          detail_asso: payload.detail_asso || "",
          is_favorite: false,
        });

        setCreateColumn(null);
        await loadBoard();

        if (createdAssociation?.id) {
          openAssociation(createdAssociation);
        }

        toast.success("Carte créée.");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la création de la carte.");
      } finally {
        setSaving(false);
      }
    },
    [columns, createColumn, loadBoard, openAssociation],
  );

  const handleConfirmDeleteAssociation = useCallback(async () => {
    if (!associationToDelete?.id) return;

    try {
      setSaving(true);

      await deleteAssociation(associationToDelete.id);

      setAssociationToDelete(null);

      setSelectedAssociation((current) =>
        Number(current?.id) === Number(associationToDelete.id) ? null : current,
      );

      setExpandedCardIds((current) => {
        const next = new Set(current);
        next.delete(String(associationToDelete.id));
        return next;
      });

      await loadBoard();

      toast.success("Association supprimée.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression.");
    } finally {
      setSaving(false);
    }
  }, [associationToDelete, loadBoard]);

  return {
    board,
    stats,
    columns,
    filteredColumns,
    loading,
    detailLoading,
    saving,

    filters,
    updateFilter,
    resetFilters,

    produitOptions,
    disciplineOptions,

    allCardsExpanded,
    expandedCardIds,
    toggleAllCards,
    toggleCardExpanded,

    selectedAssociation,
    setSelectedAssociation,
    openAssociation,

    associationToDelete,
    setAssociationToDelete,

    createColumn,
    setCreateColumn,

    loadBoard,
    handleDragEnd,
    handleSaveAssociation,
    handleCreateAssociation,
    handleConfirmDeleteAssociation,
  };
}